'use client';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { m, useInView, AnimatePresence } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock, TypedObject } from 'sanity';
import { components, paragraphs } from './PortableTextComponents';
import { Check } from '@phosphor-icons/react';
import { MenuItemLink } from '@/types/types';
import { IKButton } from '../shared/material';
import { fadeUpRight } from '@/assets/framer/animations';
import YouTube, { YouTubeProps } from 'react-youtube';
import { backgroundClasses, sectionPadding } from '../utils/utils';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      type: 'spring',
    },
  },
};

const block = {
  hidden: { opacity: 0, y: 100 },
  show: { opacity: 1, y: 0 },
};

export default function TwoColTextImage(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    textContent?: PortableTextBlock;
    content?: TypedObject | TypedObject[];
    type: 'image' | 'video';
    video?: string;
    image?: {
      url?: string;
      alt?: string;
      mediaAlt?: string;
      blurDataUrl?: string;
    };
    button: {
      text: string;
      link?: MenuItemLink;
    };
    badgesGridStyle?: boolean;
    badges?: { text: string }[];
    displayOptions: {
      showBackgroundPattern: boolean;
      background: 'white' | 'charcoal' | 'custom' | 'white-yellow';
      customBackground?: {
        type: 'gradient';
        gradientStops: string[];
      };
      padding: 'py' | 'pt' | 'pb' | 'p-0';
      reverseTextImagePositionInMobile: boolean;
      reverseTextImagePositionInDesktop: boolean;
      variant: 'default' | 'campaign';
    };
  };
}) {
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const textContent = props.data?.textContent;
  const content = props.data?.content;
  const type = props.data?.type;
  const video = props.data?.video;
  const image = props.data?.image;
  const badges = props.data?.badges;
  const badgesGridStyle = props.data?.badgesGridStyle;
  const button = props.data?.button;
  const dispalyOptions = props.data?.displayOptions;

  const section = useRef(null);
  const isInView = useInView(section, { once: true, amount: 0.3 });

  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const backgroundClass = backgroundClasses(dispalyOptions);

  const opts: YouTubeProps['opts'] = {
    height: '390',
    width: '640',
    iframeClassName: 'w-full h-full',
  };

  const setActiveVideoHandler = () => {
    if (video) {
      setActiveVideo(video);
    }
  };

  useEffect(() => {
    if (activeVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [activeVideo]);

  const onPlayerReady: YouTubeProps['onReady'] = (event) => {
    event.target.playVideo();
  };

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (
        (event.key === 'Escape' && activeVideo) ||
        (event.key === 'Esc' && activeVideo)
      ) {
        setActiveVideo(null);
      }
    };

    window.addEventListener('keydown', handleEscKeyPress);

    return () => {
      window.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [activeVideo]);

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className={`${sectionPadding(dispalyOptions?.padding)} relative z-10 scroll-m-16 ${backgroundClass}`}
      >
        {/* Top Divider wave */}
        {dispalyOptions?.background === 'charcoal' && (
          <div className='absolute left-0 top-0 h-max w-full -translate-y-[calc(100%_-_2px)] transform overflow-hidden'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-[200%] fill-charcoal-900 md:w-full'
              width='1464'
              height='34'
              viewBox='0 0 1464 34'
              fill='none'
              preserveAspectRatio='none'
            >
              <path
                className='fill-charcoal-900'
                d='M0 34H1464V13.9567C1462.14 14.0919 1459.76 14.2755 1456.86 14.4993L1456.79 14.5044C1400.39 18.8525 1148.67 38.2577 731.531 12.6924C315.789 -12.7874 65.0374 7.19974 7.26685 11.8046C4.35205 12.0369 1.92871 12.23 0 12.3763V34Z'
                fill='none'
              />
            </svg>
          </div>
        )}
        <div
          className={`container-padding container relative mx-auto ${dispalyOptions?.padding === 'py' || dispalyOptions?.padding === 'pb' ? 'pb-12' : ''}`}
        >
          {dispalyOptions?.showBackgroundPattern && (
            <m.div
              className='absolute right-0 top-0 z-0 hidden md:block'
              variants={fadeUpRight}
              animate={isInView ? 'visible' : 'hidden'}
              transition={{ delay: 0.3 }}
            >
              {dispalyOptions?.background === 'charcoal' ? (
                <svg
                  className='relative -translate-x-10 -translate-y-10 transform'
                  xmlns='http://www.w3.org/2000/svg'
                  width='169'
                  height='162'
                  viewBox='0 0 169 162'
                  fill='none'
                >
                  <path
                    d='M91.8636 73.1413C92.3412 73.1413 92.7233 72.7588 92.7233 72.2807V60.1176C92.7233 60.1176 92.6851 60.0794 92.6851 60.0411H104.703V72.013C104.703 72.4911 105.085 72.8927 105.582 72.8927H117.351V84.3864C117.351 84.8645 117.752 85.2661 118.23 85.2661H129.617V96.4156H118.23C117.752 96.4156 117.351 96.7981 117.351 97.2953V108.789H105.773C105.295 108.789 104.894 109.172 104.894 109.669V121.813C104.894 121.813 104.932 121.87 104.932 121.889H92.9717C93.029 121.775 93.1055 121.679 93.1055 121.564V109.401C93.1055 108.923 92.7233 108.521 92.2457 108.521H80.6865V97.1041C80.6865 96.626 80.3044 96.2435 79.8267 96.2435H68.0956V85.5912C68.0956 85.5912 68.0383 85.4765 68.0191 85.4191H79.5401C80.0178 85.4191 80.3999 85.0366 80.3999 84.5585V73.1413H91.8827H91.8636ZM42.3597 71.5157C41.882 71.5157 41.4999 71.8982 41.4999 72.3763V84.5394C41.4999 84.5394 41.5572 84.6542 41.5763 84.7115H29.5586C29.0809 84.7115 28.6797 85.1131 28.6797 85.5912V97.7352C28.6797 98.2133 29.0618 98.6149 29.5586 98.6149H41.8629V109.267C41.8629 109.745 42.245 110.128 42.7227 110.128H54.0526V121.545C54.0526 122.023 54.4347 122.406 54.9124 122.406H66.9301C66.8155 122.578 66.6817 122.769 66.6817 122.979V134.645C66.6817 135.257 67.1785 135.774 67.7899 135.774H79.158C79.158 135.774 79.1198 135.812 79.1198 135.85V148.013C79.1198 148.491 79.5019 148.893 79.9987 148.893H92.0355V161.4C92.0355 161.687 92.2648 161.917 92.5514 161.917H105.41C105.696 161.917 105.926 161.687 105.926 161.4V148.893H117.58C118.058 148.893 118.459 148.491 118.459 148.013V135.85C118.459 135.85 118.44 135.812 118.44 135.774H130.305C130.916 135.774 131.413 135.276 131.413 134.645V122.979C131.413 122.845 131.318 122.769 131.279 122.654H143.393C143.87 122.654 144.272 122.272 144.272 121.775V110.281H156.06C156.538 110.281 156.939 109.879 156.939 109.401V97.9265H168.097C168.575 97.9265 168.976 97.544 168.976 97.0467V84.8836C168.976 84.4055 168.575 84.0039 168.097 84.0039H156.576V72.1851C156.576 71.707 156.194 71.3054 155.697 71.3054H143.909V59.7925C143.909 59.3144 143.526 58.9319 143.03 58.9319H131.738C131.738 58.9319 131.776 58.8745 131.776 58.8554V47.2087C131.776 46.5967 131.279 46.0803 130.649 46.0803H118.001C118.058 45.9656 118.134 45.8891 118.134 45.7552V33.5921C118.134 33.114 117.733 32.7124 117.256 32.7124H105.563V20.2625C105.563 19.9756 105.333 19.7461 105.047 19.7461H92.1884C91.9018 19.7461 91.6725 19.9756 91.6725 20.2625V32.7698H79.6357C79.158 32.7698 78.7568 33.1523 78.7568 33.6495V45.8126C78.7568 45.9465 78.8523 46.023 78.8905 46.1377H67.2931C66.6626 46.1377 66.1659 46.6349 66.1659 47.266V58.9128C66.1659 59.0466 66.2614 59.1231 66.2996 59.2379H54.5111C54.0335 59.2379 53.6323 59.6204 53.6323 60.0985V71.5157H42.3023H42.3597Z'
                    fill='#51504D'
                  />
                  <path
                    d='M26.2136 25.4736H14.043V14.0564H26.2136V25.4736ZM0.515865 27.2331H13.1641V38.9754C13.1641 39.2623 13.3934 39.4918 13.68 39.4918H26.653C26.9396 39.4918 27.1689 39.2623 27.1689 38.9754V26.296H39.6834C39.97 26.296 40.1992 26.0665 40.1992 25.7796V12.7751C40.1992 12.4882 39.97 12.2587 39.6834 12.2587H27.0351V0.516358C27.0351 0.229492 26.8059 0 26.5193 0H13.5462C13.2596 0 13.0304 0.229492 13.0304 0.516358V13.1958H0.515865C0.229273 13.1958 0 13.4253 0 13.7122V26.6976C0 26.9845 0.229273 27.214 0.515865 27.214'
                    fill='#51504D'
                  />
                </svg>
              ) : (
                <svg
                  className='relative -translate-x-10 transform'
                  xmlns='http://www.w3.org/2000/svg'
                  width='233'
                  height='203'
                  viewBox='0 0 233 203'
                  fill='none'
                >
                  <path
                    d='M63.2408 133.367L51.2557 133.344L51.2779 121.952L63.263 121.976L63.2408 133.367ZM76.3157 121.095L64.773 121.073L64.7964 109.057L76.3391 109.079L76.3157 121.095ZM89.7149 107.495L77.6292 107.472L77.653 95.2748L89.7387 95.2984L89.7149 107.495ZM103.173 94.4993L90.8262 94.4753L90.852 81.2518L103.199 81.2759L103.173 94.4993ZM116.171 80.4558L104.085 80.4323L104.111 67.5711L116.196 67.5947L116.171 80.4558ZM129.859 94.2696L117.773 94.246L117.798 81.3043L129.884 81.3279L129.859 94.2696ZM143.367 107.318L131.281 107.295L131.305 95.0976L143.391 95.1211L143.367 107.318ZM156.653 120.688L144.568 120.665L144.59 108.911L156.676 108.934L156.653 120.688ZM144.901 135.298L156.987 135.321L156.964 146.713L144.879 146.69L144.901 135.298ZM131.563 148.274L143.649 148.297L143.625 160.494L131.54 160.471L131.563 148.274ZM104.263 175.05L116.349 175.074L116.324 187.935L104.238 187.911L104.263 175.05ZM91.0586 160.955L103.406 160.979L103.38 174.203L91.0328 174.179L91.0586 160.955ZM77.9124 147.908L89.9981 147.931L89.9743 160.128L77.8886 160.105L77.9124 147.908ZM89.3227 133.297L77.8605 133.275L77.8823 122.085L89.3445 122.107L89.3227 133.297ZM103.001 120.845L90.9156 120.822L90.9385 109.068L103.024 109.091L103.001 120.845ZM129.968 120.898L117.621 120.874L117.644 108.858L129.991 108.882L129.968 120.898ZM117.932 146.637L117.954 135.507L130.08 135.53C130.08 135.53 130.221 135.47 130.301 135.43L130.279 146.661L117.932 146.637ZM116.008 133.349L103.922 133.326L103.944 122.135L116.03 122.159L116.008 133.349ZM91.2491 135.455L103.335 135.478L103.314 146.347L91.2279 146.323L91.2491 135.455ZM104.597 147.96L116.944 147.984L116.92 160.442L104.573 160.418L104.597 147.96ZM142.974 133.664L130.848 133.64C130.848 133.64 130.707 133.7 130.626 133.74L130.649 121.926L142.997 121.95L142.974 133.684L142.974 133.664ZM116.661 107.528L104.314 107.504L104.339 95.045L116.686 95.0691L116.661 107.528ZM65.0864 135.686L76.6291 135.708L76.6084 146.315L65.0657 146.293L65.0864 135.686ZM130.065 174.255L117.979 174.231L118.004 161.29L130.09 161.313L130.065 174.255ZM169.659 133.736L157.573 133.712L157.596 121.978L169.681 122.002L169.659 133.736ZM37.1627 121.18C36.8611 121.179 36.6193 121.42 36.6187 121.722L36.5923 135.268C36.5917 135.57 36.8326 135.812 37.1342 135.812L50.4465 135.838L50.424 147.411C50.4234 147.713 50.6642 147.955 50.9658 147.956L63.2526 147.98L63.2285 160.358C63.2279 160.66 63.4687 160.902 63.7704 160.902L76.399 160.927L76.3731 174.17C76.3726 174.472 76.6134 174.714 76.915 174.715L89.604 174.74L89.5777 188.245C89.5771 188.547 89.8179 188.789 90.1195 188.789L103.17 188.815L103.145 201.978C103.144 202.28 103.385 202.522 103.687 202.522L117.22 202.549C117.522 202.549 117.764 202.308 117.764 202.006L117.79 188.843L130.358 188.868C130.66 188.868 130.902 188.627 130.902 188.325L130.928 175.082L144.099 175.107C144.401 175.108 144.643 174.867 144.644 174.565L144.669 161.322L157.66 161.347C157.962 161.347 158.203 161.106 158.204 160.804L158.228 148.326L170.998 148.351C171.299 148.351 171.541 148.11 171.542 147.808L171.566 135.35L183.692 135.373C183.993 135.374 184.235 135.133 184.236 134.831L184.262 121.285C184.263 120.984 184.022 120.742 183.72 120.741L171.232 120.717L171.257 107.916C171.258 107.614 171.017 107.372 170.715 107.371L157.946 107.346L157.97 94.8678C157.971 94.5659 157.73 94.3239 157.429 94.3233L144.438 94.298L144.464 81.0544C144.464 80.7525 144.223 80.5105 143.922 80.5099L130.75 80.4842L130.776 67.2608C130.777 66.9589 130.536 66.7169 130.234 66.7163L117.666 66.6918L117.692 53.5288C117.692 53.2269 117.451 52.9849 117.15 52.9843L103.616 52.9579C103.315 52.9573 103.073 53.1984 103.072 53.5003L103.047 66.6634L89.9956 66.6379C89.694 66.6373 89.4522 66.8784 89.4516 67.1803L89.4253 80.6855L76.7363 80.6608C76.4347 80.6602 76.1929 80.9012 76.1924 81.2031L76.1665 94.4467L63.5379 94.4221C63.2363 94.4215 62.9945 94.6626 62.9939 94.9645L62.9698 107.343L50.6831 107.319C50.3814 107.318 50.1397 107.559 50.1391 107.861L50.1131 121.205L37.1627 121.18Z'
                    fill='#D64C19'
                  />
                  <path
                    d='M191.162 28.1507C191.464 28.1513 191.706 27.9103 191.706 27.6084L191.731 14.8277L204.943 14.8535L204.918 27.6341C204.918 27.936 205.158 28.178 205.46 28.1786L217.767 28.2026L217.741 41.3656L204.972 41.3407C204.67 41.3402 204.428 41.5812 204.428 41.8831L204.402 54.9858L191.975 54.9616L192 41.9193C192.001 41.6174 191.76 41.3754 191.458 41.3748L178.347 41.3492L178.373 28.1258L191.142 28.1507L191.162 28.1507ZM164.169 42.1066L177.28 42.1321L177.254 55.5769C177.253 55.8788 177.494 56.1208 177.795 56.1214L191.128 56.1474L191.103 69.1897C191.102 69.4916 191.343 69.7336 191.644 69.7342L205.319 69.7608C205.62 69.7614 205.862 69.5204 205.863 69.2185L205.888 56.0957L218.658 56.1205C218.959 56.1211 219.201 55.8801 219.202 55.5782L219.228 42.1333L231.997 42.1582C232.299 42.1588 232.541 41.9178 232.541 41.6159L232.568 27.9496C232.568 27.6477 232.328 27.4057 232.026 27.4051L219.719 27.3812L219.745 13.9967C219.746 13.6948 219.505 13.4528 219.203 13.4522L205.77 13.4261L205.795 0.625289C205.796 0.323384 205.555 0.0813902 205.253 0.0808024L191.599 0.0541948C191.298 0.053607 191.056 0.294661 191.055 0.596566L191.03 13.3571L177.597 13.3309C177.296 13.3303 177.054 13.5714 177.053 13.8733L177.027 27.3181L164.278 27.2933C163.976 27.2927 163.734 27.5337 163.734 27.8356L163.707 41.5019C163.707 41.8038 163.948 42.0457 164.249 42.0463'
                    fill='#D64C19'
                  />
                  <path
                    d='M27.5986 44.6382L14.789 44.6132L14.8124 32.5974L27.622 32.6223L27.5986 44.6382ZM0.568131 46.4573L13.8804 46.4832L13.8564 58.8412C13.8558 59.1431 14.0966 59.3851 14.3983 59.3857L28.0725 59.4123C28.3742 59.4129 28.616 59.1719 28.6166 58.87L28.6426 45.5258L41.8141 45.5514C42.1158 45.552 42.3575 45.311 42.3581 45.0091L42.3848 31.3227C42.3854 31.0208 42.1445 30.7788 41.8429 30.7782L28.5306 30.7523L28.5547 18.3943C28.5553 18.0924 28.3144 17.8504 28.0128 17.8498L14.3385 17.8232C14.0369 17.8226 13.7951 18.0637 13.7945 18.3656L13.7685 31.7098L0.59692 31.6841C0.295281 31.6835 0.0535216 31.9246 0.0529333 32.2265L0.0263022 45.8927C0.0257139 46.1946 0.266532 46.4366 0.568171 46.4372'
                    fill='#D64C19'
                  />
                </svg>
              )}
            </m.div>
          )}

          <div
            className={`relative z-10 flex ${dispalyOptions?.reverseTextImagePositionInMobile ? 'flex-col-reverse' : 'flex-col'} items-center gap-10 ${dispalyOptions?.reverseTextImagePositionInDesktop ? 'md:flex-row-reverse' : 'md:flex-row'} md:gap-20`}
          >
            <div className='flex-grow space-y-5 md:w-1/2'>
              <div className='space-y-5'>
                {heading && (
                  <h2
                    className={`heading-xl relative z-10 font-display font-medium tracking-tighter ${
                      dispalyOptions?.background === 'charcoal'
                        ? 'text-yellow-150 [&_strong]:text-yellow'
                        : 'text-charcoal [&_strong]:text-pink'
                    }`}
                  >
                    <PortableText value={heading} components={components} />
                  </h2>
                )}
                {textContent && (
                  <div
                    className={`text-base sm:text-lg lg:text-2xl ${
                      dispalyOptions?.background === 'charcoal'
                        ? 'text-white'
                        : 'text-charcoal'
                    }`}
                  >
                    <PortableText value={textContent} components={paragraphs} />
                  </div>
                )}
                {badges && badges.length > 0 && (
                  <m.div
                    initial='hidden'
                    variants={container}
                    animate={isInView ? 'show' : 'hidden'}
                    className={
                      badgesGridStyle
                        ? `grid gap-3 md:grid-cols-2`
                        : `flex flex-wrap items-center gap-5 pt-5`
                    }
                  >
                    {badges.map((badge, index) => (
                      <Fragment key={`badge-${index}`}>
                        <m.div
                          variants={block}
                          className={`badge badge-md flex items-center gap-2 rounded px-3.5 py-2.5 text-lg font-medium ${
                            dispalyOptions?.background === 'charcoal'
                              ? 'bg-charcoal-800/50 text-white'
                              : 'bg-charcoal text-charcoal'
                          }`}
                        >
                          <span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-yellow font-medium'>
                            <Check
                              weight='bold'
                              className='flex-shrink-0 text-base font-semibold text-black'
                            />
                          </span>
                          <span>{badge.text}</span>
                        </m.div>
                      </Fragment>
                    ))}
                  </m.div>
                )}
              </div>
              {content && <PortableText value={content} />}

              {button && button.link && button.text && (
                <div className='flex pt-5'>
                  {button.link && (
                    <IKButton
                      link={button.link}
                      color={`${
                        dispalyOptions?.background === 'charcoal' ||
                        dispalyOptions?.background === 'white-yellow'
                          ? 'yellow'
                          : 'charcoal'
                      }`}
                      size='md'
                      className='group flex min-w-[150px] items-center justify-center gap-1 text-base'
                    >
                      <span> {button.text}</span>
                      <svg
                        className='h-5 w-5 transform transition-transform duration-300 group-hover:translate-x-2'
                        xmlns='http://www.w3.org/2000/svg'
                        width='25'
                        height='25'
                        viewBox='0 0 25 25'
                        fill='none'
                      >
                        <path
                          d='M17.5854 12.5703L10.0854 20.0704C10.0157 20.14 9.93302 20.1953 9.84197 20.233C9.75093 20.2707 9.65335 20.2901 9.5548 20.2901C9.45625 20.2901 9.35867 20.2707 9.26763 20.233C9.17658 20.1953 9.09386 20.14 9.02417 20.0704C8.95449 20.0007 8.89922 19.9179 8.8615 19.8269C8.82379 19.7359 8.80438 19.6383 8.80438 19.5397C8.80438 19.4412 8.82379 19.3436 8.8615 19.2526C8.89922 19.1615 8.95449 19.0788 9.02417 19.0091L15.9945 12.0397L9.02417 5.07035C8.88344 4.92962 8.80438 4.73875 8.80438 4.53972C8.80438 4.3407 8.88344 4.14983 9.02417 4.0091C9.1649 3.86837 9.35578 3.78931 9.5548 3.78931C9.75382 3.78931 9.94469 3.86837 10.0854 4.0091L17.5854 11.5091C17.6552 11.5788 17.7105 11.6615 17.7482 11.7525C17.786 11.8436 17.8054 11.9412 17.8054 12.0397C17.8054 12.1383 17.786 12.2359 17.7482 12.3269C17.7105 12.418 17.6552 12.5007 17.5854 12.5703Z'
                          fill='currentColor'
                        />
                      </svg>
                    </IKButton>
                  )}
                </div>
              )}
            </div>
            <div
              className={`w-full flex-grow md:w-1/3 ${dispalyOptions?.variant !== 'campaign' ? 'md:pl-5' : ''}`}
            >
              {image && image.url && (
                <>
                  <Image
                    onClick={() => setActiveVideoHandler()}
                    alt={image.mediaAlt || image.alt || ''}
                    width='800'
                    height='800'
                    src={image.url}
                    placeholder='blur'
                    blurDataURL={image.blurDataUrl}
                    className={`rounded-xl ${dispalyOptions?.variant === 'campaign' ? 'w-full object-cover md:h-[30rem] md:w-[30rem] md:object-contain' : 'w-full object-cover'} ${type === 'video' && video ? 'cursor-pointer' : ''}`}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        {/* Bottom Divider wave */}
        {dispalyOptions?.background === 'charcoal' && (
          <div className='absolute bottom-0 left-0 h-max w-full translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-[200%] md:w-full [&_path]:fill-charcoal'
              width='1464'
              height='26'
              viewBox='0 0 1464 26'
              fill='none'
              preserveAspectRatio='none'
            >
              <path
                d='M0 0V13.4014C71.6641 8.27045 311.629 -4.51068 731.531 15.2506C1153.28 35.099 1393.51 22.1175 1464 17.0324V0H0Z'
                fill='none'
              />
            </svg>
          </div>
        )}
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <m.div
            initial={false}
            className={`fixed left-0 top-0 z-[99999] h-screen w-screen bg-black/90`}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className='container-padding container relative flex h-full items-center justify-center'>
              <button
                className='absolute right-5 top-10 z-[100] rounded-full bg-white/10 px-4 py-4 text-white/90 transition duration-200 hover:bg-white/20 md:right-0'
                onClick={() => setActiveVideo(null)}
              >
                <svg
                  className='h-4 w-4 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  width='23'
                  height='23'
                  viewBox='0 0 23 23'
                  fill='none'
                >
                  <path
                    d='M21.5362 1.30971C21.8319 1.60542 21.9981 2.00649 21.9981 2.42469C21.9981 2.84289 21.8319 3.24396 21.5362 3.53967L3.69651 21.3794C3.4008 21.6751 2.99973 21.8412 2.58153 21.8412C2.16333 21.8412 1.76226 21.6751 1.46654 21.3794C1.17083 21.0837 1.0047 20.6826 1.0047 20.2644C1.0047 19.8462 1.17083 19.4451 1.46654 19.1494L19.3063 1.30971C19.602 1.014 20.003 0.847866 20.4212 0.847866C20.8394 0.847866 21.2405 1.014 21.5362 1.30971Z'
                    fill='currentColor'
                  />
                  <path
                    d='M21.5367 21.3807C21.241 21.6764 20.8399 21.8425 20.4217 21.8425C20.0035 21.8425 19.6025 21.6764 19.3068 21.3807L1.46705 3.54099C1.17134 3.24528 1.00521 2.84421 1.00521 2.42601C1.00521 2.00781 1.17134 1.60674 1.46705 1.31103C1.76276 1.01532 2.16383 0.849186 2.58203 0.849186C3.00023 0.849186 3.4013 1.01532 3.69701 1.31103L21.5367 19.1507C21.8324 19.4465 21.9986 19.8475 21.9986 20.2657C21.9986 20.6839 21.8324 21.085 21.5367 21.3807Z'
                    fill='currentColor'
                  />
                </svg>
              </button>
              {/* @ts-ignore  */}
              <YouTube
                videoId={activeVideo}
                opts={opts}
                className='aspect-video w-full max-w-4xl overflow-hidden rounded-md bg-black [aspect-ratio:_16_/_9]'
                iframeClassName='aspect-video h-full w-full [aspect-ratio:16_/_9]'
                onReady={onPlayerReady}
              />
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
